import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import { createAppVersion, listAppVersions } from '../../api/appVersions';
import { listUsers } from '../../api/users';
import { API_BASE_URL } from '../../config';

const emptyForm = { versionCode: '', versionName: '', updateType: 'soft', releaseNotes: '' };

export default function AppUpdatesSection({ token }) {
  const [versions, setVersions] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [apkFile, setApkFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [versionsData, usersData] = await Promise.all([
        listAppVersions(token),
        listUsers(token),
      ]);
      setVersions(versionsData);
      setUsers(usersData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploaderName = (userId) => users.find((u) => u.user_id === userId)?.name || `#${userId}`;

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!apkFile) {
      setSaveError('Select an APK file');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const formData = new FormData();
      formData.append('version_code', form.versionCode);
      formData.append('version_name', form.versionName);
      formData.append('update_type', form.updateType);
      if (form.releaseNotes) formData.append('release_notes', form.releaseNotes);
      formData.append('apk', apkFile);

      await createAppVersion(token, formData);
      setForm(emptyForm);
      setApkFile(null);
      setSaveSuccess('New version published');
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">App Updates</h2>

      <form
        onSubmit={handleUpload}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Version code</label>
            <input
              required
              type="number"
              min="1"
              value={form.versionCode}
              onChange={(e) => setForm({ ...form, versionCode: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Version name</label>
            <input
              required
              value={form.versionName}
              onChange={(e) => setForm({ ...form, versionName: e.target.value })}
              placeholder="e.g. 1.1.0"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Update type</label>
          <select
            value={form.updateType}
            onChange={(e) => setForm({ ...form, updateType: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="soft">Soft (dismissible)</option>
            <option value="force">Force (blocks app until updated)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Release notes (optional)
          </label>
          <textarea
            rows={2}
            value={form.releaseNotes}
            onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">APK file</label>
          <input
            required
            type="file"
            accept=".apk"
            onChange={(e) => setApkFile(e.target.files?.[0] || null)}
            className="w-full text-sm"
          />
        </div>

        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saveSuccess && <p className="text-sm text-indigo-600">{saveSuccess}</p>}

        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Uploading...' : 'Publish version'}
        </button>
      </form>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Version</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Code</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Type</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Uploaded by</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Uploaded at</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">APK</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : versions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  No versions published yet
                </td>
              </tr>
            ) : (
              versions.map((v) => (
                <tr key={v.app_version_id}>
                  <td className="px-4 py-2 text-gray-900">{v.version_name}</td>
                  <td className="px-4 py-2 text-gray-600">{v.version_code}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        v.update_type === 'force'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {v.update_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{uploaderName(v.uploaded_by)}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                    {new Date(v.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <a
                      href={`${API_BASE_URL}/app-versions/${v.app_version_id}/download`}
                      className="text-indigo-600 hover:underline"
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}
